/**
 * M10 P4a2a (plan 2026-10-11-m10-p4a §3.2; spec 2026-10-09-hull-tiers-design §4.8, §4.9): the yard rules as pure
 * functions. "May this yard build this job for this buyer, and at what price": `canBuild` by yard level, the trade
 * relation (the embargo), the outlaw rule and the player's mapping by standing or company relation.
 *
 * Nothing calls these yet (no caller = identity). P4a2b wires `canBuildAt`, `yardRefuses` and `surchargeMult` into the
 * AI yard choice (`queueAwareYard`, `placeOrder`); P4a3a reads the same switches for the war-start cancellation;
 * P4a3b calls `playerRelation`/`playerSurchargeMult` in `buyShipAt`, and the shop's price line calls the state-free
 * `playerRelationOf`/`playerSurchargeMult` on the main thread (review m-3).
 *
 * No state writes and no rng. The AI functions run per order decision in the day slice (D-014): they allocate
 * nothing per call. The per-data masks are built once (`masksOf`, a WeakMap memo like `catalogueOf`); every other
 * function reads typed arrays and numbers only.
 *
 * Bands are bits `1 << band` (`HULL_BAND_IDS` order: S 1, M 2, L 4, XL 8). A hull outside `hull-lines` (`HULL_NONE`
 * band) gets `NO_BAND_BIT`, which every level allows and no rule refuses, so a row can never read empty because of it.
 */
import { HULL_BAND_IDS, type HullLines } from '../hullLines'
import { HULL_NONE, type StrategicData } from '../data'
import { catalogueOf, catalogueRow, toolingOf } from '../catalogue'
import { inLadder, relationOf } from '../factions/ladder'
import { RELATION, type StrategicState } from '../types'

/** The embargo rules (`hull-lines.json embargo`). */
export type EmbargoRules = HullLines['embargo']

/** The bit of a hull with no band (not in `hull-lines`): allowed at every level, never refused. */
export const NO_BAND_BIT = 1 << HULL_BAND_IDS.length

/** Station levels are a `Uint8Array` column, so a mask per possible level value. */
const LEVELS = 256

interface YardMasks {
  /** By yard level: the band bits whose `minYardLevel ≤ level` (plus `NO_BAND_BIT`). */
  readonly level: Uint8Array
  /** The band bits a yard refuses at skirmish (`embargo.skirmishRefuses`). */
  readonly skirmish: number
}

const masks = new WeakMap<object, YardMasks>()

function buildMasks(data: StrategicData): YardMasks {
  const sizes = data.hullLines.sizes
  const level = new Uint8Array(LEVELS)
  for (let l = 0; l < LEVELS; l++) {
    let m = NO_BAND_BIT
    for (let b = 0; b < HULL_BAND_IDS.length; b++) if (sizes[HULL_BAND_IDS[b]].minYardLevel <= l) m |= 1 << b
    level[l] = m
  }
  let skirmish = 0
  for (const band of data.hullLines.embargo.skirmishRefuses) skirmish |= 1 << HULL_BAND_IDS.indexOf(band)
  const out: YardMasks = { level, skirmish }
  masks.set(data, out)
  return out
}

function masksOf(data: StrategicData): YardMasks {
  const hit = masks.get(data)
  if (hit !== undefined) return hit
  return buildMasks(data)
}

/** The band bit of hull band index `band` (`NO_BAND_BIT` for `HULL_NONE` or anything out of range). */
export function bandBit(band: number): number {
  if (band === HULL_NONE || band >= HULL_BAND_IDS.length) return NO_BAND_BIT
  return 1 << band
}

/** The band bits a yard of `level` builds: every band whose `sizes[band].minYardLevel ≤ level` (memoised per data). */
export function levelBandMask(data: StrategicData, level: number): number {
  if (level < 0) return NO_BAND_BIT
  return masksOf(data).level[level < LEVELS ? level : LEVELS - 1]
}

/**
 * The OR of the band bits of the hulls in the catalogue row (tooling of `yard`, `buyer`, `job`); −1 when there is no
 * row to read (catalogue off, or `buyer` outside the catalogue's faction axis). `job` is a catalogue job (a `ROLE`
 * value or `JOB_ESCORT`).
 */
export function rowBandMask(state: StrategicState, yard: number, buyer: number, job: number): number {
  const cat = catalogueOf(state.data)
  if (cat === null || buyer < 0 || buyer >= cat.factionCapacity) return -1
  const row = catalogueRow(cat, toolingOf(state, yard, buyer), buyer, job)
  const band = state.data.hullBand
  const base = row * cat.slots
  let m = 0
  for (let k = 0; k < cat.len[row]; k++) m |= bandBit(band[cat.hull[base + k]])
  return m
}

/**
 * `canBuild` (spec §4.9; plan §0.3: computed, nothing stored): the yard's level allows at least one band of the row's
 * hulls. Off (`canBuild.enabled` false) → true. No row to read (catalogue off) → true.
 */
export function canBuildAt(state: StrategicState, yard: number, buyer: number, job: number): boolean {
  if (!state.data.hullLines.canBuild.enabled) return true
  if (yard < 0 || yard >= state.stations.count) return false
  const row = rowBandMask(state, yard, buyer, job)
  if (row < 0) return true
  return (row & levelBandMask(state.data, state.stations.level[yard])) !== 0
}

/**
 * The relation that sets the terms of an AI `buyer`'s order at a yard owned by `owner` (spec §4.8): peace for the
 * owner's own orders, for an outlaw buyer (the outlaw rule: peace terms at any yard; P4a1b's rows hold only S/M export
 * patterns for them), and while `embargo.enabled` is false; else the M5 relation.
 */
export function tradeRelation(state: StrategicState, buyer: number, owner: number): number {
  if (buyer === owner) return RELATION.peace
  if (state.data.charter.outlaw[buyer] === 1) return RELATION.peace
  if (!state.data.hullLines.embargo.enabled) return RELATION.peace
  return relationOf(state, buyer, owner)
}

/**
 * The yard refuses `buyer`'s `job` order: at war always; at skirmish when the job's row holds only bands in
 * `embargo.skirmishRefuses` (no P4a row does; P4b5 adds the masked pick for mixed rows); else never.
 */
export function yardRefuses(state: StrategicState, buyer: number, yard: number, job: number): boolean {
  const rel = tradeRelation(state, buyer, state.stations.owner[yard])
  if (rel === RELATION.war) return true
  if (rel !== RELATION.skirmish) return false
  const row = rowBandMask(state, yard, buyer, job)
  if (row <= 0) return false
  return (row & ~masksOf(state.data).skirmish) === 0
}

/**
 * M10 P4a W5c (D-TBD): the last-resort yard rule. True when `embargo.warFallback` is on and `yard` refuses `buyer`'s
 * `job` only because its owner is at war (`tradeRelation` war; the yard can build the job, and the row is not
 * L/XL-only, so skirmish terms would take it). `queueOrderYard` asks this only when no yard takes the order normally,
 * so the war yard is the job's last resort. False while `embargo.enabled` is off (`tradeRelation` is peace).
 */
export function warFallbackTakes(state: StrategicState, buyer: number, yard: number, job: number): boolean {
  if (!state.data.hullLines.embargo.warFallback) return false
  if (tradeRelation(state, buyer, state.stations.owner[yard]) !== RELATION.war) return false
  if (!canBuildAt(state, yard, buyer, job)) return false
  const row = rowBandMask(state, yard, buyer, job)
  return row <= 0 || (row & ~masksOf(state.data).skirmish) !== 0
}

/**
 * The AI order's price multiplier at `owner`'s yard: `1 + economicSurcharge` at economic relations, else 1. M10 P4a W5c:
 * also at war, where an order is placed only through the last-resort rule (`warFallbackTakes`), so a war order never
 * costs less than an economic one.
 */
export function surchargeMult(state: StrategicState, buyer: number, owner: number): number {
  const rel = tradeRelation(state, buyer, owner)
  if (rel !== RELATION.economic && rel !== RELATION.war) return 1
  return 1 + state.data.hullLines.embargo.economicSurcharge
}

/**
 * The player's relation with a yard (spec §4.8's mapping; pure, no state, so the main-thread shop model calls it too).
 * Off → peace. `companyRelation ≥ 0` (a founded company in the ladder and an owner in the ladder) → that relation.
 * Else personal standing with the owner: below `warBelow` war, below `skirmishBelow` skirmish, below `economicBelow`
 * economic, else peace (strict `<` at each threshold).
 */
export function playerRelationOf(standing: number, companyRelation: number, embargo: EmbargoRules): number {
  if (!embargo.enabled) return RELATION.peace
  if (companyRelation >= 0) return companyRelation
  const p = embargo.player
  if (standing < p.warBelow) return RELATION.war
  if (standing < p.skirmishBelow) return RELATION.skirmish
  if (standing < p.economicBelow) return RELATION.economic
  return RELATION.peace
}

/**
 * The player's relation with `owner`'s yard. `corp` is the player's faction slot, `standing` the player's personal
 * standing with `owner` (the Career's). The company's M5 relation counts only when both `corp` and `owner` are in the
 * ladder: the sole trader is an outlaw (not in the ladder), and the referee (the Authority) is never in it, so its
 * yards use personal standing even after founding (review m-4). The player is never an outlaw buyer here.
 */
export function playerRelation(state: StrategicState, corp: number, owner: number, standing: number): number {
  const company = inLadder(state, corp) && inLadder(state, owner) ? relationOf(state, corp, owner) : -1
  return playerRelationOf(standing, company, state.data.hullLines.embargo)
}

/** The player's price multiplier for relation `rel` (pure): `1 + economicSurcharge` at economic, else 1. */
export function playerSurchargeMult(rel: number, embargo: EmbargoRules): number {
  return rel === RELATION.economic ? 1 + embargo.economicSurcharge : 1
}
